// The contact specialisation of the generic reference value (PEOPLE-RECORD-1, D-202.1).
//
// A matched contact stores `{ display, ref }` where `ref` is a `contacts.id`. A contact the
// planner typed and did not match stores `{ display }` with no `ref` — which is a complete and
// correct value, not a degraded one (the s201 ruling: free text always wins). The control
// creates the row on commit, so an unmatched name is the transient case rather than the normal
// one, but the tolerance is unconditional and every reader depends on it.
//
// Pure — no React, no DOM. Everything here delegates to `shared/reference-value.ts`; this file
// adds naming and the contacts-row meaning, never a second parse.
//
// 🔴 The ONE shape difference from `shared/institution.ts`: an institution `ref` is a stable
// registry SLUG we author, a contact `ref` is a per-client row UUID the product mints. Nothing
// in the value channel cares, but it is why the resolve path must check the row belongs to the
// same client — a pointer is only meaningful inside the estate that owns it.
//
// Out of scope by ruling: `address` and `dob` on the record (deferred, s201 amendment 4 and
// s204 ruling 1), and linking `legal.beneficiaries` to a contact row (a later build).

import {
  formatReferenceSummary,
  parseReference,
  toReferenceValue,
  type ReferenceValue,
} from "./reference-value";

/** A stored contact value. `ref`, when present, is a `contacts.id`. */
export type ContactRef = ReferenceValue;

/** Parse a stored contact value, or null when it is not a reference object
 *  (a plain string typed before the fieldType flip, or empty). */
export function parseContact(value: string | null | undefined): ContactRef | null {
  return parseReference(value);
}

/** The contact name to DISPLAY. A plain string passes through unchanged, so a legacy value
 *  renders exactly as it always did (G8). */
export function formatContactSummary(value: string | null | undefined): string {
  return formatReferenceSummary(value);
}

/** Build the value to store. `id` is the `contacts.id` when the planner picked a suggestion or
 *  the control minted a row, and omitted when neither happened. */
export function toContactValue(name: string, id?: string): string {
  return toReferenceValue(name, id);
}

/**
 * Split a typed name into the FIRST NAME(S) + SURNAME the record stores (s201 amendment 2).
 *
 * The rule, stated because it is a choice and not an obvious one: split on the LAST space —
 * everything before it is `firstName`, the final token is `surname`. A single token goes to
 * `surname` alone, with `firstName` empty.
 *
 * That single-token case is also the ORGANISATION case, and it is the reason `surname` is the
 * NOT NULL column: "Bloor West Property Management" is one name, not a first name and a family
 * name, exactly as `legal.ts:296` has labelled the field "Surname (or organisation name)" since
 * BENEFICIARY-FILL-1. A person with one name lands in the same place and is not wrong there.
 *
 * 🔴 This is a display-and-storage convenience, never a correctness claim about human names.
 * The planner can edit either part on the record afterwards; nothing re-derives the split from
 * the typed string a second time.
 */
export function splitContactName(typed: string): { firstName: string; surname: string } {
  const trimmed = typed.trim().replace(/\s+/g, " ");
  if (trimmed === "") return { firstName: "", surname: "" };
  const lastSpace = trimmed.lastIndexOf(" ");
  if (lastSpace === -1) return { firstName: "", surname: trimmed };
  return { firstName: trimmed.slice(0, lastSpace), surname: trimmed.slice(lastSpace + 1) };
}

/** Compose the display string from a record's stored parts — the inverse of `splitContactName`,
 *  and the ONLY place a full name is assembled. Never stored; a stored copy goes stale, which is
 *  the defect PEOPLE-RECORD-1 exists to end. */
export function composeContactName(
  contact: { firstName?: string | null; surname?: string | null },
): string {
  return [contact.firstName ?? "", contact.surname ?? ""].map((p) => p.trim()).filter(Boolean).join(" ");
}
